import axios from 'axios';
import { fileForUpload } from './uploadFilename';

/**
 * Upload one or more files with aggregate byte-weighted progress (0–100).
 * @param {Object} options
 * @param {string} options.apiUrl - Base API URL
 * @param {string} options.token - Auth token
 * @param {Array<File|{ file: File, fileType?: string }>} options.files
 * @param {(percent: number) => void} [options.onProgress]
 * @returns {Promise<Array<{ data: object, fileType?: string }>>}
 */
export async function uploadFilesWithProgress({ apiUrl, token, files, onProgress, timeout }) {
  if (!files?.length) {
    return [];
  }

  const entries = files.map((item) => {
    if (item?.file instanceof File || item?.file instanceof Blob) {
      return { file: item.file, fileType: item.fileType };
    }
    return { file: item, fileType: undefined };
  });

  const fileSizes = entries.map(({ file }) => file?.size || 0);
  const totalBytes = fileSizes.reduce((sum, size) => sum + size, 0);
  const perFilePercent = new Array(entries.length).fill(0);

  const reportProgress = () => {
    if (!onProgress) return;
    if (totalBytes > 0) {
      let loadedWeighted = 0;
      perFilePercent.forEach((pct, index) => {
        loadedWeighted += (pct / 100) * fileSizes[index];
      });
      onProgress(Math.min(100, Math.round((loadedWeighted / totalBytes) * 100)));
      return;
    }
    const average = perFilePercent.reduce((sum, pct) => sum + pct, 0) / perFilePercent.length;
    onProgress(Math.min(100, Math.round(average)));
  };

  onProgress?.(0);

  const encoder = new TextEncoder();
  const uploadPromises = entries.map(async ({ file, fileType }, index) => {
    const ready = fileForUpload(file);
    const filename = String(ready?.name || file?.name || `upload-${index}`).replace(/"/g, '');
    const mime = String(ready?.type || file?.type || 'application/octet-stream')
      .split(';')[0]
      .trim() || 'application/octet-stream';
    const fileBytes = new Uint8Array(
      typeof ready?.arrayBuffer === 'function'
        ? await ready.arrayBuffer()
        : ready,
    );
    if (!fileBytes.byteLength) {
      throw new Error('Voice note audio is empty');
    }

    // Build a fixed-length multipart body. Chromium/Electron FormData often
    // uses a chunked stream and fails with OnSizeReceived Error: -2.
    const boundary = `----WorkloopUpload${Date.now()}${index}`;
    const header = encoder.encode(
      `--${boundary}\r\nContent-Disposition: form-data; name="file"; filename="${filename}"\r\nContent-Type: ${mime}\r\n\r\n`,
    );
    const footer = encoder.encode(`\r\n--${boundary}--\r\n`);
    const body = new Uint8Array(header.length + fileBytes.length + footer.length);
    body.set(header, 0);
    body.set(fileBytes, header.length);
    body.set(footer, header.length + fileBytes.length);

    const response = await axios.post(`${apiUrl}/files/upload`, body, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        'Content-Type': `multipart/form-data; boundary=${boundary}`,
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      timeout: timeout || 0,
      onUploadProgress: (event) => {
        if (event.total) {
          perFilePercent[index] = Math.round((event.loaded / event.total) * 100);
        } else if (event.loaded > 0) {
          perFilePercent[index] = Math.min(99, perFilePercent[index] + 5);
        }
        reportProgress();
      },
    });

    perFilePercent[index] = 100;
    reportProgress();

    return { data: response.data, fileType };
  });

  const results = await Promise.all(uploadPromises);
  onProgress?.(100);
  return results;
}

export function inferAttachmentFileType(file) {
  if (file?.fileType && file.fileType !== 'FILE') {
    return file.fileType;
  }
  const fileName = file?.fileName || file?.name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)$/i.test(fileName)) {
    return 'IMAGE';
  }
  if (
    /^voice-note-/i.test(fileName)
    || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(fileName)
    || String(file?.type || '').startsWith('audio/')
  ) {
    return 'AUDIO';
  }
  if (/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i.test(fileName)) {
    return 'VIDEO';
  }
  return file?.fileType || 'FILE';
}
